// src/components/Admin/AdminLogin.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import authService from '../../services/authService';

const AdminLogin = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get the intended destination or default to admin dashboard
  const from = location.state?.from?.pathname || '/admin/dashboard';

  useEffect(() => {
    // If user is already authenticated, redirect to dashboard
    if (authService.isAuthenticated()) {
      navigate(from, { replace: true });
    }
    
    // Load saved email if remember me was checked
    const savedEmail = localStorage.getItem('adminEmail');
    if (savedEmail) {
      setFormData(prev => ({ ...prev, email: savedEmail }));
      setRememberMe(true);
    }
  }, [navigate, from]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const result = await authService.signIn(formData.email, formData.password);
      
      if (result.success) {
        // Save email if remember me is checked
        if (rememberMe) {
          localStorage.setItem('adminEmail', formData.email);
        } else {
          localStorage.removeItem('adminEmail');
        }
        
        // Redirect to intended destination
        navigate(from, { replace: true });
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!formData.email) {
      setError('Please enter your email address first');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const result = await authService.sendPasswordReset(formData.email);
      
      if (result.success) {
        alert('Password reset email sent! Please check your inbox.');
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to send password reset email');
      console.error('Password reset error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login-container">
      <div className="container-fluid vh-100">
        <div className="row h-100">
          {/* Left side - Branding */}
          <div className="col-lg-6 d-none d-lg-flex align-items-center justify-content-center bg-primary">
            <div className="text-center text-white">
              <img 
                src={`${process.env.PUBLIC_URL}/assets/img/Logo-Straight.png`} 
                alt="Digisofts Logo" 
                className="mb-4"
                style={{ maxHeight: '80px', filter: 'brightness(0) invert(1)' }}
              />
              <h2 className="mb-3" style={{color: 'white'}}>Welcome to Admin Panel</h2>
              <p className="lead">
                Manage your blog content with our powerful admin interface
              </p>
              <div className="mt-4">
                <i className="bi bi-shield-check" style={{ fontSize: '3rem' }}></i>
              </div>
            </div>
          </div>
          
          {/* Right side - Login Form */}
          <div className="col-lg-6 d-flex align-items-center justify-content-center">
            <div className="login-form-container" style={{ maxWidth: '400px', width: '100%' }}>
              <div className="text-center mb-4 d-lg-none">
                <img 
                  src={`${process.env.PUBLIC_URL}/assets/img/Logo-Straight.png`} 
                  alt="Digisofts Logo" 
                  style={{ maxHeight: '60px' }}
                />
              </div>
              
              <div className="card shadow-lg border-0">
                <div className="card-body p-5">
                  <h3 className="card-title text-center mb-4">Admin Login</h3>
                  
                  {error && (
                    <div className="alert alert-danger" role="alert">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      {error}
                    </div>
                  )}
                  
                  <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                      <label htmlFor="email" className="form-label">
                        <i className="bi bi-envelope me-2"></i>
                        Email Address
                      </label>
                      <input
                        type="email"
                        className="form-control form-control-lg"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="Enter your email"
                        required
                        disabled={loading}
                      />
                    </div>
                    
                    <div className="mb-3">
                      <label htmlFor="password" className="form-label">
                        <i className="bi bi-lock me-2"></i>
                        Password
                      </label>
                      <div className="input-group">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          className="form-control form-control-lg"
                          id="password"
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          placeholder="Enter your password"
                          required
                          disabled={loading}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => setShowPassword(!showPassword)}
                          disabled={loading}
                        >
                          <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                        </button>
                      </div>
                    </div>
                    
                    <div className="mb-3 d-flex justify-content-between align-items-center">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="rememberMe"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          disabled={loading}
                        />
                        <label className="form-check-label" htmlFor="rememberMe">
                          Remember me
                        </label>
                      </div>
                      
                      <button
                        type="button"
                        className="btn btn-link p-0 text-decoration-none"
                        onClick={handleForgotPassword}
                        disabled={loading}
                      >
                        Forgot Password?
                      </button>
                    </div>
                    
                    <button
                      type="submit"
                      className="btn btn-primary btn-lg w-100"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Signing In...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-box-arrow-in-right me-2"></i>
                          Sign In
                        </>
                      )}
                    </button>
                  </form>
                  
                  <div className="text-center mt-4">
                    <small className="text-muted">
                      <i className="bi bi-info-circle me-1"></i>
                      Admin access only. Contact support if you need help.
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <style jsx="true">{`
        .admin-login-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }
        
        .bg-primary {
          background: linear-gradient(135deg, #7b00ff 0%, #6a00e6 100%) !important;
        }
        
        .card {
          border-radius: 15px;
        }
        
        .form-control:focus {
          border-color: #7b00ff;
          box-shadow: 0 0 0 0.2rem rgba(123, 0, 255, 0.25);
        }
        
        .btn-primary {
          background-color: #7b00ff;
          border-color: #7b00ff;
        }
        
        .btn-primary:hover {
          background-color: #6a00e6;
          border-color: #6a00e6;
        }
        
        .btn-primary:focus {
          box-shadow: 0 0 0 0.2rem rgba(123, 0, 255, 0.25);
        }
        
        .alert-danger {
          border-radius: 10px;
        }
        
        @media (max-width: 991.98px) {
          .admin-login-container {
            padding: 20px;
          }
        }
      `}</style>
    </div>
  );
};

export default AdminLogin;